import { createFormHook, useStore } from '@tanstack/react-form';
import { Button } from './button';
import type React from 'react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { fieldContext, formContext, useFormContext } from './form/shared';
import { FormReadOnlyProvider, useFormReadOnly } from './form/form-read-only';
import { FormOptionalLabelProvider } from './form/form-optional-label';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Skeleton } from './skeleton';
import { Spinner } from './spinner';
export type { OtherImageItem } from './form/multi-image-upload-field';

import TextField from './form/text-field';
import TextareaField from './form/textarea-field';
import NumberField from './form/number-field';
import CurrencyField from './form/currency-field';
import SuffixTextField from './form/suffix-text-field';
import CheckboxField from './form/checkbox-field';
import SwitchField from './form/switch-field';
import PasswordField from './form/password-field';
import LinkTextField from './form/link-text-field';
// Native so browsers autofill them; eager, since a chunk cost a request and a flash for as many bytes.
import NativeSelectTextField from './form/native-select-text-field';
import NativeCountryField from './form/native-country-field';
import NativeRegionField from './form/native-region-field';
import NativeProvinceField from './form/native-province-field';

// Height is 20 (label row) + 8 (gap) + the control, as in the real field.
function StandardFieldSkeleton({ controlClassName }: { controlClassName?: string }) {
	return (
		<div className="grid w-full gap-2">
			<div className="flex h-5 items-center">
				<Skeleton className="h-3.5 w-24" />
			</div>
			<Skeleton className={cn('h-8 w-full rounded-lg', controlClassName)} />
		</div>
	);
}

function StackedFieldSkeleton({ controlClassName }: { controlClassName?: string }) {
	return (
		<div className="w-full">
			<div className="mb-2 flex h-5 items-center">
				<Skeleton className="h-3.5 w-24" />
			</div>
			<Skeleton className={cn('w-full rounded-xl', controlClassName)} />
		</div>
	);
}

// A ~198px drop zone when editing; ~20px "No file" or a ~58px chip row when read-only.
function UploadFieldSkeleton({ editingClassName = 'h-[198px]' }: { editingClassName?: string } = {}) {
	const isReadOnly = useFormReadOnly();
	return (
		<div className="w-full">
			<div className="mb-2 flex h-5 items-center">
				<Skeleton className="h-3.5 w-24" />
			</div>
			<Skeleton className={cn('w-full rounded-xl', isReadOnly ? 'h-12' : editingClassName)} />
		</div>
	);
}

// One h-14 row per language in `availableLanguages`.
const LANGUAGES_PICKER_ROWS = ['SPA', 'ENG', 'FRA', 'DEU', 'ITA', 'POR', 'CAT', 'EUS', 'GLG', 'NLD', 'PRT'] as const;

function LanguagesPickerFieldSkeleton() {
	return (
		<>
			{LANGUAGES_PICKER_ROWS.map((code) => (
				<Skeleton key={code} className="h-14 w-full rounded-lg" />
			))}
		</>
	);
}

function lazyField<P extends object>(
	loader: () => Promise<{ default: React.ComponentType<P> }>,
	fallback: React.ReactNode = <StandardFieldSkeleton />,
): React.ComponentType<P> {
	const LazyComponent = lazy(loader);
	return function LazyField(props: P) {
		return (
			<Suspense fallback={fallback}>
				<LazyComponent {...props} />
			</Suspense>
		);
	};
}

const EditorField = lazyField(() => import('./form/editor-field'), <StackedFieldSkeleton controlClassName="h-48" />);
const SimpleEditorField = lazyField(
	() => import('./form/simple-editor-field'),
	<StackedFieldSkeleton controlClassName="h-32" />,
);
const SelectTextField = lazyField(() => import('./form/select-text-field'));
const SelectNumberField = lazyField(() => import('./form/select-number-field'));
const AltTextField = lazyField(() => import('./form/alt-text-field'));
const DateField = lazyField(() => import('./form/date-field'));
const DateTimeField = lazyField(() => import('./form/datetime-field'));
const ColorField = lazyField(() => import('./form/color-field'));
const FileField = lazyField(() => import('./form/file-field'));
const ImageField = lazyField(() => import('./form/image-field'));
const ImageUploadField = lazyField(() => import('./form/image-upload-field'), <UploadFieldSkeleton />);
const MultiImageUploadField = lazyField(() => import('./form/multi-image-upload-field'), <UploadFieldSkeleton />);
const VideoUploadField = lazyField(() => import('./form/video-upload-field'), <UploadFieldSkeleton />);
const DocumentUploadField = lazyField(() => import('./form/document-upload-field'), <UploadFieldSkeleton />);
const SdgField = lazyField(() => import('./form/sdg-field'), <StackedFieldSkeleton controlClassName="h-8" />);
const LanguagesPickerField = lazyField(() => import('./form/languages-picker-field'), <LanguagesPickerFieldSkeleton />);
const CountryField = lazyField(() => import('./form/country-field'));
const MultiCountryField = lazyField(() => import('./form/multi-country-field'));
const RegionField = lazyField(() => import('./form/region-field'));
const MultiCountryRegionField = lazyField(() => import('./form/multi-country-region-field'));
const ProvinceField = lazyField(() => import('./form/province-field'));
const ComboboxNumberField = lazyField(() => import('./form/combobox-number-field'));
const MultiComboboxTextField = lazyField(() => import('./form/multi-combobox-text-field'));
const MultiComboboxNumberField = lazyField(() => import('./form/multi-combobox-number-field'));
const SeoTextField = lazyField(() => import('./form/seo-text-field'));
const SlugField = lazyField(() => import('./form/slug-field'));

export const MAX_SIZE = 4 * 1024 * 1024;

export const downloadFile = (blob: Blob, filename: string) => {
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const fileToBase64 = async (file: File | undefined): Promise<string | undefined> =>
	file instanceof File
		? new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				const handleLoad = () => resolve(reader.result as string);
				reader.addEventListener('load', handleLoad);
				reader.addEventListener('error', reject);
			})
		: undefined;

export const downloadCsvTemplate = (headers: string[], fileName: string) => {
	const csv = headers.join(',');
	// The BOM is the only way Excel reads UTF-8: https://stackoverflow.com/a/18925211/6261347
	const blob = new Blob(['\ufeff', csv]);
	const downloadLink = document.createElement('a');
	const url = URL.createObjectURL(blob);
	downloadLink.href = url;
	downloadLink.download = `${fileName}.csv`;
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
};

export const getIdOrGenerate = (id: string | undefined): string => (!id || id === '' ? crypto.randomUUID() : id);

// `children` renders after the label (an arrow badge); left in the spread, the explicit children win.
function SubmitButton({ label, children, ...props }: React.ComponentProps<typeof Button> & { label: string }) {
	const form = useFormContext();
	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Button {...props} disabled={props.disabled ?? isSubmitting}>
					{isSubmitting && <Spinner />}
					{label}
					{children}
				</Button>
			)}
		</form.Subscribe>
	);
}

function SaveDiscardButtons(props: {
	className?: string;
	unsavedChangesText?: string;
	discardText?: string;
	saveText?: string;
	invalidText?: string;
}) {
	const form = useFormContext();
	const { isSubmitting, isDefaultValue, canSubmit } = useStore(form.store, (state) => ({
		isDefaultValue: state.isDefaultValue,
		isSubmitting: state.isSubmitting,
		canSubmit: state.canSubmit,
	}));

	// An untouched invalid form (a legacy record with bad data) would ignore Save with no feedback.
	const handleInvalidSave = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (canSubmit) return;
		event.preventDefault();
		for (const name of Object.keys(form.state.fieldMeta)) {
			form.setFieldMeta(name as never, (meta) => ({ ...meta, isTouched: true }));
		}
		const formElement = event.currentTarget.closest('form');
		requestAnimationFrame(() => {
			formElement?.querySelector('[data-slot="form-message"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
		});
		toast.error(props.invalidText ?? 'Please review the highlighted fields before saving.');
	};

	const unsavedText = props.unsavedChangesText ?? 'You have unsaved changes, are you sure you want to leave?';

	useBlocker({
		shouldBlockFn: () => {
			if (isDefaultValue) return false;

			const shouldLeave = confirm(unsavedText);
			return !shouldLeave;
		},
		enableBeforeUnload: !isDefaultValue,
	});

	if (isDefaultValue) return null;

	return (
		<div className={cn('flex gap-2', props.className)}>
			<Button
				type="reset"
				variant="secondary"
				onClick={(event) => {
					event.preventDefault();
					form.reset();
					// TODO: a second reset clears the translated campaign texts; a tanstack/form bug.
					setTimeout(() => form.reset());
				}}
			>
				{props.discardText ?? 'Discard'}
			</Button>
			<Button disabled={isSubmitting} onClick={handleInvalidSave}>
				{isSubmitting && <Spinner />}
				{props.saveText ?? 'Save'}
			</Button>
		</div>
	);
}

export { FormReadOnlyProvider, useFormReadOnly, FormOptionalLabelProvider };

const { useAppForm: _useAppForm, withForm } = createFormHook({
	fieldComponents: {
		TextField,
		TextareaField,
		NumberField,
		CurrencyField,
		DateField,
		DateTimeField,
		ColorField,
		FileField,
		ImageField,
		SelectTextField,
		SelectNumberField,
		CountryField,
		MultiCountryField,
		MultiCountryRegionField,
		RegionField,
		NativeSelectTextField,
		NativeCountryField,
		NativeRegionField,
		NativeProvinceField,
		ProvinceField,
		ComboboxNumberField,
		MultiComboboxTextField,
		MultiComboboxNumberField,
		CheckboxField,
		SwitchField,
		PasswordField,
		SuffixTextField,
		EditorField,
		SimpleEditorField,
		ImageUploadField,
		DocumentUploadField,
		VideoUploadField,
		MultiImageUploadField,
		AltTextField,
		LinkTextField,
		SeoTextField,
		SlugField,
		SdgField,
		LanguagesPickerField,
	},
	formComponents: {
		SubmitButton,
		SaveDiscardButtons,
	},
	fieldContext,
	formContext,
});

const useAppFormWithScrollToError: typeof _useAppForm = (...args) => {
	const form = _useAppForm(...args);

	const submissionAttempts = useStore(form.store, (state: { submissionAttempts: number }) => state.submissionAttempts);
	const prevAttempts = useRef(0);
	useEffect(() => {
		if (submissionAttempts > prevAttempts.current) {
			prevAttempts.current = submissionAttempts;
			setTimeout(() => {
				const firstError = document.querySelector<HTMLElement>('[aria-invalid="true"]');
				if (firstError) {
					firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
					firstError.focus({ preventScroll: true });
				}
			}, 0);
		}
	}, [submissionAttempts]);

	return form;
};

export const useAppForm = useAppFormWithScrollToError;
export { withForm };
